import dayjs, { Dayjs } from "dayjs";
import locale from "dayjs/locale/ru";

import { doIt } from "@/utils/doIt";
import formatter from "@/utils/formatter";
import { asInteger, Integer } from "@/utils/integer";
import { isPassed } from "@/utils/isPassed";
import { plural } from "@/utils/plural";
import round from "@/utils/round";

import { SubscriptionInvoice } from "../../invoice/domain/Invoice";
import { Interval } from "../../payment-system/infrastructure/tiptop/types/types";
import { IPromocode, PromocodeType } from "../../promocode/domain/Promocode";
import { PeriodItem } from "../data/paymentPlans";
import { IPaymentPlan } from "../domain/PaymentPlan";
import { ISubscription } from "../domain/Subscription";

//

dayjs.locale(locale);

//

export interface PriceData {
  price: Integer;
  intervalPrice: number;
  oldPrice: Integer | null;
  discount: number | null;
}

export interface PaymentData {
  d: Date;
  isUpgrade: boolean;
  isEnablingAutoRenewal: boolean;
  firstPayment: {
    amount: Integer;
    date: Date;
    payNow: boolean;
    activateNow: boolean;
    daysToPay: number;
    startPaidDaysAt: Date;
    planActivatedAt: Date;
    firstIntervalDays: number;
    nextIntervals: number;
  };
  nextPayment: {
    amount: Integer;
    date: Date;
  };
  priceData: PriceData;
  periodItem: PeriodItem;
  plan: IPaymentPlan;
  promocode: IPromocode | null;
  trialDays: number;
  promocodeDiscount: number;
}

export interface GetPeriodStringOptions {
  silentOne?: boolean;
  pluralizingMap?: Partial<Record<Interval, [string, string, string]>>;
}

//

const INTERVALS_IN_MONTH: Record<Interval, number> = {
  Day: 30,
  Week: 30 / 7,
  Month: 1,
};

const DAYJS_INTERVALS: Record<Interval, dayjs.ManipulateType> = {
  Day: "days",
  Month: "months",
  Week: "weeks",
};

const DAYS_IN_INTERVALS = {
  Day: 1,
  Week: 7,
  Month: 30,
} as const satisfies Record<Interval, number>;

const MONTHS = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

//

export interface PaymentCalculatorProps {
  plan: IPaymentPlan;
  currentPlan: IPaymentPlan;
  periodItem: PeriodItem;
  currentSubscription: ISubscription;
  currentInvoiceData: SubscriptionInvoice;
  promocode?: IPromocode | null;
}

//

export class PaymentCalculator {
  private D = PaymentCalculator.setTime(dayjs());

  private plan: IPaymentPlan;
  private periodItem: PeriodItem;
  private currentSubscription: ISubscription;
  private currentPlan: IPaymentPlan;
  private currentInvoiceData: SubscriptionInvoice;
  private promocode: IPromocode | null;

  private intervalsInMonth: number;
  private dayjsInterval: dayjs.ManipulateType;
  private daysInInterval: number;

  public static authorizeAmount = asInteger(100);

  //

  constructor(props: PaymentCalculatorProps) {
    this.plan = props.plan;
    this.currentPlan = props.currentPlan;
    this.periodItem = props.periodItem;
    this.currentSubscription = props.currentSubscription;
    this.currentInvoiceData = props.currentInvoiceData;
    this.promocode = props.promocode ?? null;

    const { interval } = this.periodItem;
    this.intervalsInMonth = INTERVALS_IN_MONTH[interval];
    this.dayjsInterval = DAYJS_INTERVALS[interval];
    this.daysInInterval = DAYS_IN_INTERVALS[interval];
  }

  // PUBLIC

  public calcPriceData(): PriceData {
    const price = this.calcPrice();
    const intervalPrice = price / this.periodItem.period;
    const oldPrice = this.calcOldPrice(price);
    const discount = this.calcDiscount(price, oldPrice);
    return { price, intervalPrice, oldPrice, discount };
  }

  //

  public calcPaymentData(): PaymentData {
    const { trialDays, discount, promocode } = this.handlePromocode();

    const paymentDatesData = this.calcPaymentDates();
    const { NextDate, FirstDate, PlanActivatedAt } = paymentDatesData;
    const isUpgrade = this.isUpgrade();
    const isEnablingAutoRenewal = this.isEnablingAutoRenewal();
    const firstPaymentData = this.calcFirstPayment(FirstDate, NextDate);
    const { firstIntervalDays, firstPayment, nextIntervals } = firstPaymentData;
    const priceData = this.calcPriceData();

    return {
      d: this.D.toDate(),
      isUpgrade,
      isEnablingAutoRenewal,
      firstPayment: {
        amount: firstPayment,
        date: FirstDate.toDate(),
        payNow: paymentDatesData.firstPaymentNow,
        activateNow: paymentDatesData.planActivatedNow,
        daysToPay: paymentDatesData.daysToPay,
        planActivatedAt: PlanActivatedAt.toDate(),
        startPaidDaysAt: FirstDate.toDate(),
        firstIntervalDays,
        nextIntervals,
      },
      nextPayment: {
        amount: priceData.price,
        date: NextDate.toDate(),
      },
      priceData,
      periodItem: this.periodItem,
      plan: this.plan,
      promocode: promocode,
      trialDays: trialDays,
      promocodeDiscount: discount,
    };
  }

  //

  public isUpgrade() {
    const isEnablingAutoRenewal = this.isEnablingAutoRenewal();
    return this.plan.monthlyPrice >= this.currentPlan.monthlyPrice && !isEnablingAutoRenewal;
  }

  //

  public isEnablingAutoRenewal() {
    return this.plan.type === this.currentPlan.type;
  }

  //

  public calcDaysToExpired(D: dayjs.Dayjs): Integer {
    const expiresAt = this.currentSubscription.expiresAt;
    const daysToExpired = expiresAt && Math.ceil(dayjs(expiresAt).diff(D, "days", true));
    const integer = doIt(() => {
      if (daysToExpired === null) return asInteger(0);
      if (daysToExpired <= 0) return asInteger(0);
      return asInteger(daysToExpired);
    });
    return integer;
  }

  // PRIVATE

  private calcPrice(
    monthlyPrice: number = this.plan.monthlyPrice,
    periodDiscount: number = this.periodItem.discount,
  ): Integer {
    const { period } = this.periodItem;
    const intervalPrice = monthlyPrice / this.intervalsInMonth;
    const periodPrice = intervalPrice * period;
    const discountedPeriodPrice = periodPrice * (1 - periodDiscount);
    return PaymentCalculator.roundPayment(discountedPeriodPrice);
  }

  //

  private calcOldPrice(periodPrice: Integer): Integer | null {
    const monthlyOldPrice = this.plan.oldPrice ?? this.plan.monthlyPrice;
    const periodOldPrice = this.calcPrice(monthlyOldPrice, 0);
    if (periodPrice >= periodOldPrice) return null;
    return periodOldPrice;
  }

  //

  private calcDiscount(price: Integer, oldPrice: Integer | null): number | null {
    if (oldPrice === null) return null;
    return round.afterSign(1 - price / oldPrice, 2);
  }

  //

  private calcPaymentDates() {
    const { trialDays } = this.handlePromocode();
    const D = PaymentCalculator.setTime(this.D.add(trialDays, "days"));

    const isUpgrade = this.isUpgrade();
    const expiresAt = this.currentSubscription.expiresAt ?? D;
    const isExpired = isPassed(expiresAt);

    const PlanActivatedAt = doIt(() => {
      if (isUpgrade) return D;
      if (isExpired) return D;
      return PaymentCalculator.setTime(dayjs(expiresAt));
    });

    const FirstDate = doIt(() => {
      if (!isUpgrade) return PlanActivatedAt;
      const daysToUpgrade = this.calcDaysToUpgrade(D);
      return D.add(daysToUpgrade, "days");
    });

    const NextDate = doIt(() => {
      return this.calcNextStartOfIntervalDate(FirstDate);
    });

    const daysToPay = Math.ceil(NextDate.diff(FirstDate, "days", true));
    const firstPaymentNow = FirstDate.isSame(this.D);
    const planActivatedNow = PlanActivatedAt.isSame(this.D);

    return {
      daysToPay,
      NextDate,
      FirstDate,
      PlanActivatedAt,
      firstPaymentNow,
      planActivatedNow,
    };
  }

  //

  private calcFirstPayment(StartPaidDaysAt: Dayjs, NextDate: Dayjs) {
    const EndOfInterval = doIt(() => {
      const EndOfInterval = StartPaidDaysAt.endOf(this.dayjsInterval);
      if (EndOfInterval.isAfter(NextDate)) return NextDate;
      return EndOfInterval;
    });
    const firstIntervalDays = Math.ceil(EndOfInterval.diff(StartPaidDaysAt, "days", true));
    const nextIntervals = NextDate.diff(EndOfInterval, this.dayjsInterval);

    const { intervalPrice } = this.calcPriceData();
    const firstIntervalPayment = PaymentCalculator.roundPayment(
      this.calcProratedAmount(
        firstIntervalDays,
        intervalPrice,
        this.periodItem.interval,
        StartPaidDaysAt.daysInMonth(),
      ),
    );
    const otherIntervalPayment = PaymentCalculator.roundPayment(nextIntervals * intervalPrice);
    const firstPayment = asInteger(firstIntervalPayment + otherIntervalPayment);

    return {
      firstIntervalDays,
      nextIntervals,
      firstPayment,
    };
  }

  //

  private calcNextStartOfIntervalDate(D: dayjs.Dayjs, _period = this.periodItem.period) {
    const dayjsInterval = this.dayjsInterval;
    const period: number = doIt(() => {
      const daysInCurrentInterval = this.calcDaysToUpgrade(this.D);
      const daysInNewInterval = this.daysInInterval * _period;
      const daysDiff = daysInNewInterval - daysInCurrentInterval;
      const remainingIntervals = Math.ceil(daysDiff / this.daysInInterval);
      if (remainingIntervals <= 0) return 1;
      if (remainingIntervals >= this.periodItem.period) return this.periodItem.period;
      return remainingIntervals;
    });
    const NextStartOfInterval = D.startOf(dayjsInterval).add(period, dayjsInterval);
    return PaymentCalculator.setTime(NextStartOfInterval);
  }

  //

  private calcDaysToUpgrade(D: dayjs.Dayjs): Integer {
    const { discount } = this.handlePromocode();
    const daysToExpired = this.calcDaysToExpired(D);

    const currentBalance = doIt(() => {
      const { paymentPlanMonthlyPrice } = this.currentInvoiceData;
      const remainingBalance = (paymentPlanMonthlyPrice / DAYS_IN_INTERVALS.Month) * daysToExpired;
      return remainingBalance + discount;
    });

    const daysToUpgrade = doIt(() => {
      const { period } = this.periodItem;
      const newPlanPrice = this.calcPrice();
      if (newPlanPrice === 0) return 0;
      const availableIntervals = currentBalance / newPlanPrice;
      return availableIntervals * this.daysInInterval * period;
    });

    return asInteger(Math.floor(daysToUpgrade));
  }

  //

  private calcProratedAmount(
    daysToPay: number,
    intervalAmount: number,
    interval: Interval,
    daysInMonth: number = DAYS_IN_INTERVALS.Month,
  ): number {
    const daysInIntervalMap: Record<Interval, number> = {
      Day: DAYS_IN_INTERVALS.Day,
      Week: DAYS_IN_INTERVALS.Week,
      Month: daysInMonth,
    };
    const daysInInterval = daysInIntervalMap[interval];
    return (intervalAmount / daysInInterval) * daysToPay;
  }

  //

  private handlePromocode(): {
    trialDays: number;
    discount: number;
    isPercentage: boolean;
    promocode: IPromocode | null;
  } {
    const isUpgrade = this.isUpgrade();
    const promocode = isUpgrade ? this.promocode : null;

    const trialDays = doIt(() => {
      if (!promocode) return 0;
      if (promocode.type !== "trial-days") return 0;
      return promocode.value;
    });

    const discount = doIt(() => {
      if (!promocode) return 0;
      if (promocode.type === "discount-amount") return promocode.value;
      if (promocode.type === "discount-percentage") return promocode.value;
      return 0;
    });

    const isPercentage = promocode?.type === "discount-percentage";

    return { trialDays, discount, isPercentage, promocode };
  }

  // STATIC

  public static setTime(D: dayjs.Dayjs) {
    return D.set("hour", 12).set("minute", 0).set("second", 0).set("millisecond", 0);
  }

  //

  public static calcMonthlyPrice(amount: number, interval: Interval, period: number): number {
    const daysInInterval = DAYS_IN_INTERVALS[interval];
    return (amount / (daysInInterval * period)) * DAYS_IN_INTERVALS.Month;
  }

  //

  public static roundPayment(value: number): Integer {
    return asInteger(round.byFactor(value, 100, "ceil"));
  }

  //

  public static getNowPayment(data: PaymentData): {
    amount: Integer;
    firstPaymentNow: boolean;
  } {
    const { isUpgrade } = data;
    const { payNow, amount } = data.firstPayment;
    if (!isUpgrade) return { amount: asInteger(0), firstPaymentNow: payNow };
    if (payNow) return { amount, firstPaymentNow: payNow };
    return { amount: this.authorizeAmount, firstPaymentNow: payNow };
  }

  //

  public static getText(data: PaymentData) {
    const firstPeriodString = this.getFirstPeriodString(data);
    const nextPeriodsString = this.getNextPeriodsString(data);
    const promoText = this.getPromoText(data);

    return { firstPeriodString, nextPeriodsString, promoText };
  }

  //

  private static getFirstPeriodString(data: PaymentData) {
    const { firstPayment, periodItem, d } = data;
    const { interval } = periodItem;

    const forPeriodMap: Record<Interval, string> = {
      Day: "",
      Week: "",
      Month: doIt(() => {
        const StartPaidDaysAt = dayjs(firstPayment.startPaidDaysAt);
        const month = MONTHS[StartPaidDaysAt.month()];
        const currentYear = dayjs(d).year();
        const paymentYear = StartPaidDaysAt.year();
        const theSameYear = currentYear === paymentYear;
        if (theSameYear) return month;
        return `${month} ${paymentYear}`;
      }),
    };

    const pluralMap: Record<Interval, (v: number) => string> = {
      Day: (v: number) => plural(v, "день", "дня", "дней"),
      Week: (v: number) => plural(v, "неделя", "недели", "недель"),
      Month: (v: number) => plural(v, "месяц", "месяца", "месяцев"),
    };

    const forPeriod = forPeriodMap[interval];

    const pluralPeriod = pluralMap[interval];
    const pluralized = pluralPeriod(firstPayment.nextIntervals);
    const otherIntervals = firstPayment.nextIntervals >= 1 ? ` и еще ${pluralized}` : "";

    const daysInFirstInterval = firstPayment.firstIntervalDays;
    const daysBetweenString = plural(daysInFirstInterval, "день", "дня", "дней");

    const daysStringMap: Record<Interval, string> = {
      Day: daysBetweenString,
      Week: daysBetweenString,
      // Month: ` (${daysBetweenString}${firstPayment.startPaidDaysNow ? "" : `, с ${formatter.date(firstPayment.startPaidDaysAt)}`})`,
      Month: ` (${daysBetweenString})`,
    };

    const daysString = daysStringMap[interval];
    return `${forPeriod}${daysString}${otherIntervals}`;

    // 3D: 3 дня                          | (forPeriod: "")(daysString: "3 дня")(otherPeriods: "")
    // 6W: 2 дня и еще 5 недель           | (forPeriod: "")(daysString: "2 дня")(otherPeriods: " и еще 5 недель")
    // 4M: март (17 дней) и еще 3 месяца  | (forPeriod: "Март")(daysString: "(17 дней)")(otherPeriods: " и еще 3 месяца")
  }

  //

  private static getNextPeriodsString(data: PaymentData) {
    const { periodItem } = data;
    const { interval, period } = periodItem;

    const intervalStringMap: Record<
      Interval,
      { every: [string, string, string]; interval: [string, string, string]; inOne: string }
    > = {
      Day: {
        every: ["каждый", "каждые", "каждые"],
        interval: ["день", "дня", "дней"],
        inOne: "в день",
      },
      Week: {
        every: ["каждую", "каждые", "каждые"],
        interval: ["неделю", "недели", "недель"],
        inOne: "в неделю",
      },
      Month: {
        every: ["каждый", "каждые", "каждые"],
        interval: ["месяц", "месяца", "месяцев"],
        inOne: "в месяц",
      },
    };

    const intervalStrings = intervalStringMap[interval];

    const everyString = plural(period, ...intervalStrings.every, null);
    const monthsString = plural(period, ...intervalStrings.interval);
    return period === 1 ? intervalStrings.inOne : `${everyString} ${monthsString}`;
  }

  //

  private static getPromoText(data: PaymentData) {
    if (!data.promocode) return null;

    const promoTextMap: Record<PromocodeType, string> = {
      "trial-days": `Промокод ${data.promocode.code}`,
      "discount-amount": `-${formatter.currency(data.promocodeDiscount)} по промокоду`,
      "discount-percentage": `-${formatter.percentage(data.promocodeDiscount)} по промокоду`,
    };

    return promoTextMap[data.promocode.type];
  }

  //

  public static getPeriodString(
    period: number,
    interval: Interval,
    options?: GetPeriodStringOptions,
  ) {
    const { silentOne, pluralizingMap } = options ?? {};

    const periodStringsMap: Record<Interval, [string, string, string]> = {
      Day: pluralizingMap?.Day ?? ["день", "дня", "дней"],
      Week: pluralizingMap?.Week ?? ["неделя", "недели", "недель"],
      Month: pluralizingMap?.Month ?? ["месяц", "месяца", "месяцев"],
    };

    return plural(
      period,
      ...periodStringsMap[interval],
      silentOne && period === 1 ? null : "right",
    );
  }
}
